import { useCallback } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import type z from 'zod';

import { Button } from '@components/button';
import { Form } from '@components/form';
import { InputControlled } from '@components/input/controlled';
import { Card } from '@components/ui/card';
import { pages } from '@constants/pages.constant';
import { useAuth } from '@contexts/auth';
import {
  createSessionSchema,
  type CreateSessionPayload,
} from '@services/sessions/dtos/create.dto';
import { sessionService } from '@services/sessions/service';

export const Route = createFileRoute('/_auth-layout/(auth)/sign-in')({
  component: SignIn,
});

function SignIn() {
  const { onSetAuthorization } = useAuth();
  const navigate = useNavigate();

  const form = useForm<CreateSessionPayload>({
    resolver: zodResolver(createSessionSchema),
    mode: 'onBlur',
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const { isPending, mutateAsync: signIn } = useMutation({
    mutationFn: sessionService.create,
    onError: () => {
      toast.error('Atenção', {
        description: 'E-mail e/ou senha incorretos. Tente novamente',
      });
    },
    onSuccess: ({ refreshToken, token }) => {
      onSetAuthorization({ token, refreshToken });
      navigate({
        to: '/home',
        replace: true,
      });
    },
  });

  const onSubmit = useCallback(
    async (values: z.infer<typeof createSessionSchema>) => {
      await signIn({
        email: values.email,
        password: values.password,
      });
    },
    [signIn],
  );

  return (
    <Form.Provider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Card.Content className="mt-8 mb-14 p-0">
          <div className="space-y-2">
            <InputControlled
              autoCapitalize="off"
              autoComplete="off"
              design="auth"
              formMessageProps={{
                className: 'mb-0.5 ml-auto mt-0',
              }}
              name="email"
              placeholder="E-mail"
              size="xl"
              type="email"
            />
            <InputControlled
              design="auth"
              formMessageProps={{
                className: 'mb-0.5 ml-auto mt-0',
              }}
              name="password"
              placeholder="Senha"
              secure
              size="xl"
              type="password"
            />
            <Button
              className="mt-4 w-full shadow-none"
              isLoading={isPending}
              size="xl"
              type="submit">
              ENTRAR
            </Button>
          </div>
        </Card.Content>
        <Card.Footer className="grid">
          <Link
            className="text-primary mx-auto block w-fit text-center text-sm font-light underline"
            preload={false}
            to={pages.auth.forgotPassword}>
            Esqueci minha senha
          </Link>
        </Card.Footer>
      </form>
    </Form.Provider>
  );
}
